import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RootState, AppDispatch } from "../../../../redux/store";
import { listenToShortstayData, upsertShortstay } from "../../../../redux/shortstaysSlice";
import CircularProgress from "@mui/material/CircularProgress";
import {
  CheckCircle2,
  MapPin,
  Tag,
  Plus,
  Home,
  LayoutDashboard,
} from "lucide-react";

/* ── tokens ── */
const T = {
  bg: "#F1EFE8", surface: "#ffffff", border: "#D3D1C7",
  border2: "#B4B2A9", text: "#2C2C2A", textSoft: "#5F5E5A",
  muted: "#888780", accent: "#2C2C2A", accentFg: "#F1EFE8",
  gold: "#BA7517", goldLight: "#FAEEDA",
  sans: "'Montserrat', sans-serif",
  serif: "'Cormorant Garamond', serif",
};

const FONT_CSS = `@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Montserrat:wght@300;400;500;600&display=swap');`;

const rateLabelMap: Record<string, string> = {
  perNight:          "/ night",
  perNightPerPerson: "/ night · person",
  perHour:           "/ hour",
  perHourPerPerson:  "/ hour · person",
};

const ShortForm10: React.FC = () => {
  const { userId, listingId } = useParams<{ userId: string; listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const currentListing = useSelector((s: RootState) => s.shortstays.currentListing);

  const [isPublishing, setIsPublishing] = useState(false);
  const [published,    setPublished]    = useState(false);

  useEffect(() => {
    if (listingId && (!currentListing || currentListing.listingId !== listingId)) {
      dispatch(listenToShortstayData(listingId));
    }
  }, [dispatch, listingId, currentListing]);

  /* block access if already in review — but only before publishing */
  useEffect(() => {
    if (!published && currentListing?.inReview) {
      navigate(`/shortdashboard/${userId}`);
    }
  }, [currentListing, navigate, userId, published]);

  const handlePublish = async () => {
    if (!listingId || !userId) return;
    setIsPublishing(true);
    await dispatch(upsertShortstay({ listingId, data: { isDraft: false, inReview: true } }));
    setIsPublishing(false);
    setPublished(true);
  };

  /* derived values */
  const rent         = currentListing?.rate ?? 0;
  const rateType     = currentListing?.rateType ?? "perNight";
  const extraCharges = currentListing?.extraCharges ?? 0;
  const rateLabel    = rateLabelMap[rateType] || "/ night";
  const city         = currentListing?.address?.city    || "Unknown City";
  const country      = currentListing?.address?.country || "Unknown Country";
  const photo        = currentListing?.photos?.[0];
  const title        = currentListing?.title || "Untitled listing";

  /* ── success screen ── */
  if (published) {
    return (
      <div style={s.page}>
        <style>{FONT_CSS}</style>
        <div style={s.successWrap}>
          <div style={s.successIconRing}>
            <CheckCircle2 size={36} color={T.gold} />
          </div>
          <p style={s.successTitle}>Listing submitted</p>
          <p style={s.successSub}>
            Your listing is now under review. We'll notify you once it goes live.
          </p>
          <div style={s.successActions}>
            <button
              onClick={() => navigate(`/`)}
              style={s.btnOutline}
            >
              <Home size={14} style={{ marginRight: 6 }} />
              Home
            </button>
            <button
              onClick={() => navigate(`/shortdashboard/${userId}`)}
              style={s.btnDark}
            >
              <LayoutDashboard size={14} style={{ marginRight: 6 }} />
              Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── main screen ── */
  return (
    <div style={s.page}>
      <style>{FONT_CSS}</style>

      {/* saving overlay */}
      {isPublishing && (
        <div style={s.overlay}>
          <CircularProgress size={52} sx={{ color: T.gold }} />
          <p style={{ color: "#fff", marginTop: 16, fontSize: 13, fontFamily: T.sans, letterSpacing: "0.1em" }}>
            PUBLISHING…
          </p>
        </div>
      )}

      {/* header */}
      <header style={s.header}>
        <span style={s.brand}>ARELUXE</span>
      </header>

      {/* body */}
      <div style={s.body}>

        {/* step label */}
        <p style={s.stepLabel}>Final step</p>
        <p style={s.heading}>Review &amp; publish</p>
        <p style={s.sub}>This is how your listing appears to guests. Publish when you're ready.</p>

        {/* preview card */}
        <div style={s.previewOuter}>
          <div style={s.previewCard}>
            <div style={s.imageWrap}>
              {photo
                ? <img src={photo} alt="Listing preview" style={s.image} />
                : (
                  <div style={s.imagePlaceholder}>
                    <Home size={32} color={T.muted} />
                    <span style={{ fontSize: 12, color: T.muted, marginTop: 8 }}>No photo added</span>
                  </div>
                )
              }
              {/* gold border overlay */}
              <div style={s.imageBorderOverlay} />
            </div>

            {/* info */}
            <div style={s.infoWrap}>
              <p style={s.listingTitle}>{title}</p>

              <div style={s.infoRow}>
                <MapPin size={13} color={T.muted} style={{ flexShrink: 0 }} />
                <span style={s.infoText}>{city}, {country}</span>
              </div>

              <div style={s.divider} />

              <div style={s.priceRow}>
                <div>
                  <span style={s.priceAmount}>€{rent.toLocaleString()}</span>
                  <span style={s.priceLabel}> {rateLabel}</span>
                </div>
                {extraCharges > 0 && (
                  <div style={s.extrasBadge}>
                    <Plus size={10} color={T.gold} />
                    <span>€{extraCharges.toLocaleString()} extras</span>
                  </div>
                )}
              </div>

              {/* status pill */}
              <div style={s.draftPill}>
                <Tag size={10} color={T.muted} />
                <span>Draft · not yet visible to guests</span>
              </div>
            </div>
          </div>
        </div>

        {/* checklist */}
        <div style={s.checklist}>
          {[
            "Photos uploaded",
            "Pricing configured",
            "Booking settings saved",
            "Address & details complete",
          ].map((item) => (
            <div key={item} style={s.checkRow}>
              <CheckCircle2 size={14} color={T.gold} style={{ flexShrink: 0 }} />
              <span style={s.checkText}>{item}</span>
            </div>
          ))}
        </div>

      </div>

      {/* footer */}
      <footer style={s.footer}>
        <button
          onClick={() => navigate(`/shortform9/${userId}/${listingId}`)}
          style={s.btnBack}
        >
          Back
        </button>
        <button onClick={handlePublish} disabled={isPublishing} style={s.btnPublish}>
          {isPublishing ? "Publishing…" : "Publish listing"}
        </button>
      </footer>
    </div>
  );
};

export default ShortForm10;

/* ── styles ── */
const s: Record<string, React.CSSProperties> = {
  page:        { background: T.bg, color: T.text, minHeight: "100vh", fontFamily: T.sans, position: "relative" },
  overlay:     { position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", backdropFilter: "blur(8px)", zIndex: 200, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" },

  /* header */
  header:      { position: "fixed", top: 0, left: 0, right: 0, height: 56, background: "rgba(0,0,0,.92)", backdropFilter: "blur(12px)", borderBottom: "1px solid #1c1c1c", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 },
  brand:       { color: "#fff", fontSize: 14, fontWeight: 300, letterSpacing: "0.3em", fontFamily: "'Cormorant Garamond', serif" },

  /* body */
  body:        { maxWidth: 520, margin: "0 auto", padding: "96px 20px 120px" },
  stepLabel:   { fontSize: 10, fontWeight: 500, color: T.muted, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 },
  heading:     { fontSize: 28, fontWeight: 300, color: T.text, fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.04em", marginBottom: 6 },
  sub:         { fontSize: 13, color: T.textSoft, lineHeight: 1.6, marginBottom: 32 },

  /* preview card */
  previewOuter:{ marginBottom: 24 },
  previewCard: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" },
  imageWrap:   { position: "relative", width: "100%", aspectRatio: "16/9", overflow: "hidden" },
  image:       { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  imageBorderOverlay: { position: "absolute", inset: 0, border: "1px solid rgba(186,117,23,.2)", borderRadius: 0, pointerEvents: "none" },
  imagePlaceholder: { width: "100%", height: "100%", background: T.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" },
  infoWrap:    { padding: "16px 20px 20px" },
  listingTitle:{ fontSize: 18, fontWeight: 500, color: T.text, marginBottom: 8, fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.02em" },
  infoRow:     { display: "flex", alignItems: "center", gap: 6, marginBottom: 12 },
  infoText:    { fontSize: 12, color: T.textSoft },
  divider:     { height: "0.5px", background: T.border, marginBottom: 12 },
  priceRow:    { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 },
  priceAmount: { fontSize: 22, fontWeight: 500, color: T.text, fontFamily: "'Cormorant Garamond', serif" },
  priceLabel:  { fontSize: 12, color: T.textSoft },
  extrasBadge: { display: "flex", alignItems: "center", gap: 4, background: T.goldLight, border: `0.5px solid #FAC775`, borderRadius: 6, padding: "3px 8px", fontSize: 11, color: T.gold },
  draftPill:   { display: "inline-flex", alignItems: "center", gap: 5, background: T.bg, border: `0.5px solid ${T.border}`, borderRadius: 6, padding: "4px 10px", fontSize: 11, color: T.muted },

  /* checklist */
  checklist:   { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10 },
  checkRow:    { display: "flex", alignItems: "center", gap: 10 },
  checkText:   { fontSize: 13, color: T.textSoft },

  /* footer */
  footer:      { position: "fixed", bottom: 0, left: 0, right: 0, background: T.surface, borderTop: `0.5px solid ${T.border}`, padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 100 },
  btnBack:     { padding: "10px 22px", borderRadius: 20, background: "transparent", color: T.textSoft, border: `0.5px solid ${T.border2}`, cursor: "pointer", fontSize: 13, fontFamily: T.sans },
  btnPublish:  { padding: "11px 32px", borderRadius: 20, background: T.accent, color: T.accentFg, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500, fontFamily: T.sans, letterSpacing: "0.04em" },

  /* success screen */
  successWrap:    { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 32, textAlign: "center" },
  successIconRing:{ width: 72, height: 72, borderRadius: "50%", background: T.goldLight, border: `1px solid #FAC775`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 },
  successTitle:   { fontSize: 26, fontWeight: 300, color: T.text, fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.04em", marginBottom: 8 },
  successSub:     { fontSize: 13, color: T.textSoft, lineHeight: 1.7, maxWidth: 340, marginBottom: 32 },
  successActions: { display: "flex", gap: 12 },
  btnOutline:     { display: "flex", alignItems: "center", padding: "11px 22px", borderRadius: 20, background: "transparent", color: T.textSoft, border: `0.5px solid ${T.border2}`, cursor: "pointer", fontSize: 13, fontFamily: T.sans },
  btnDark:        { display: "flex", alignItems: "center", padding: "11px 24px", borderRadius: 20, background: T.accent, color: T.accentFg, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500, fontFamily: T.sans },
};